<template>
  <div>
    <div v-if="displayLoader">
      <div class="text-center">
        <div>
          <b-spinner label="Spinning"></b-spinner>
        </div>
        <h2 class="pt-5">Loading assignment. It may take a few seconds.</h2>
      </div>
    </div>
    <NotFoundPage
      v-else-if="displayNotFound"
      title="Assignment not found"
      subtitle="The assignment you are looking for does not exit in our system. Please check if you followed correct link."
    >
      Or go to <router-link :to="{ name: assignmentsRoute }">Assignments List</router-link>
    </NotFoundPage>
    <div v-else>
      <router-view />
    </div>
  </div>
</template>

<script lang="ts">
import type { Assignment, AssignmentWsEventData, StatusType } from '@/types/Models/Assignment'
import { BSpinner } from 'bootstrap-vue-next'
import { defineComponent, onBeforeUnmount } from 'vue'
import { computed, type ComputedRef, onMounted } from 'vue'
import { onBeforeRouteLeave, useRoute } from 'vue-router'
import { LoadingStatus } from '@/const/LoadingStatus'
import { none } from '@/services/Helpers'
import { useAccountStore } from '@/store/account'
import { useAssignmentStore } from '@/store/assignment'
import { useAssignmentSummaryStore } from '@/store/assignmentSummary'
import { CUSTOMER_ASSIGNMENTS_LIST, STAFF_ASSIGNMENTS_LIST } from '@/types/router'
import NotFoundPage from '@/views/Components/NotFoundPage/NotFound.vue'

export default defineComponent({
  name: 'Assignment',
  components: { BSpinner, NotFoundPage },
  setup() {
    const accountStore = useAccountStore()
    const assignmentSummaryStore = useAssignmentSummaryStore()
    const assignmentStore = useAssignmentStore()
    const route = useRoute()

    const loadingStatus: ComputedRef<LoadingStatus> = computed(() => assignmentSummaryStore.loadingStatus)
    const assignment: ComputedRef<Assignment | null> = computed(() => assignmentSummaryStore.assignment)
    const isStaff = computed<boolean>(() => accountStore.isStaff)
    const displayLoader = computed(() => [LoadingStatus.PENDING, LoadingStatus.LOADING].includes(loadingStatus.value))
    const displayNotFound = computed(() => loadingStatus.value === LoadingStatus.FAILURE)
    const assignmentsRoute = computed(() => (isStaff.value ? STAFF_ASSIGNMENTS_LIST : CUSTOMER_ASSIGNMENTS_LIST))

    onMounted(() => {
      if (loadingStatus.value !== LoadingStatus.SUCCESS) {
        assignmentSummaryStore.fetch(route.params.id as string).catch(none)
      }
    })

    onBeforeRouteLeave((_to, _from) => {
      assignmentSummaryStore.resetState()
      return undefined
    })

    onMounted(() => {
      let lastPushed: number = 0
      /**
       * Use-cases
       *
       * - change 'validating' -> 'validation_complete'. For that we also need to update error counts, as action
       * buttons depend on whether assignment has errors or not
       */
      window.Echo.channel('App.Assignments').listen(
        '\\App\\Events\\Assignment\\AssignmentStatusChanged',
        (data: AssignmentWsEventData) => {
          if (!data.assignment?.id || !assignment.value?.id || parseFloat(data.pushed || '0') < lastPushed) {
            return
          }
          if (data.assignment?.id === assignment.value?.id) {
            lastPushed = parseFloat(data.pushed || '0')
            assignmentSummaryStore.partialUpdate({
              totalErrorsCount: data.assignment.totalErrorsCount,
              labelsErrorsCount: data.assignment.labelsErrorsCount,
              domesticErrorsCount: data.assignment.domesticErrorsCount,
              internationalErrorsCount: data.assignment.internationalErrorsCount,
              domesticItemsCount: data.assignment.domesticItemsCount,
              internationalItemsCount: data.assignment.internationalItemsCount,
              status: data.assignment.status as StatusType,
            })
            assignmentStore.refreshItems()
          }
        },
      )
    })

    onBeforeUnmount(() => {
      window.Echo.leave('App.Assignments')
    })

    return {
      displayLoader,
      displayNotFound,
      loadingStatus,
      assignmentsRoute,
    }
  },
})
</script>
